'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useDebouncedCallback } from '../hooks/use-debounce';

interface UseDataTableSearchOptions {
  /**
   * URL parameter name for the search query
   * @default 'search'
   */
  paramName?: string;
  /**
   * Debounce delay in milliseconds
   * @default 300
   */
  debounceMs?: number;
  /**
   * URL params to reset whenever the search term changes.
   * - `'page'` is set to `'1'` (offset pagination).
   * - any other key is deleted (e.g. `'cursor'` for keyset pagination).
   * @default ['page']
   */
  resetParams?: string[];
}

interface UseDataTableSearchReturn {
  /**
   * Current local search value (updates immediately on input)
   */
  query: string;
  /**
   * Current URL search value (updates after debounce)
   */
  urlQuery: string;
  /**
   * Function to update the search query
   */
  setQuery: (value: string) => void;
  /**
   * Clear the search query immediately
   */
  clearQuery: () => void;
  /**
   * Whether a search is currently being debounced
   */
  isSearching: boolean;
}

/**
 * Hook for managing DataTable search with URL-based state management.
 *
 * Features:
 * - Debounced input to reduce API calls
 * - URL parameter management (shareable/bookmarkable)
 * - Automatic pagination reset on search
 * - Loading state indicator during debounce
 * - Local state synced with URL for controlled inputs
 * - Immediate clear functionality
 *
 * @param options - Configuration options
 * @returns Object with query, urlQuery, setQuery, clearQuery, and isSearching state
 *
 * @example
 * ```tsx
 * const { query, setQuery, clearQuery, isSearching } = useDataTableSearch({
 *   paramName: 'search',
 *   debounceMs: 300,
 * });
 *
 * return (
 *   <div>
 *     <Input
 *       value={query}
 *       onChange={(e) => setQuery(e.target.value)}
 *       placeholder="Search..."
 *     />
 *     {query && <Button onClick={clearQuery}>Clear</Button>}
 *     {isSearching && <Loader2 className="animate-spin" />}
 *   </div>
 * );
 * ```
 */
export function useDataTableSearch(
  options: UseDataTableSearchOptions = {},
): UseDataTableSearchReturn {
  const {
    paramName = 'search',
    debounceMs = 300,
    resetParams = ['page'],
  } = options;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  // URL value
  const urlQuery = searchParams.get(paramName) || '';

  // Local state for controlled input
  const [localQuery, setLocalQuery] = useState(urlQuery);
  const [isSearching, setIsSearching] = useState(false);

  // Sync local state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs internal input state to external URL change (back/forward); intentional one-way sync
    setLocalQuery(urlQuery);
  }, [urlQuery]);

  const updateSearchParams = (term: string) => {
    const params = new URLSearchParams(searchParams);
    // Reset pagination state on a new search term.
    for (const key of resetParams) {
      if (key === 'page') {
        params.set('page', '1');
      } else {
        params.delete(key);
      }
    }
    if (term) {
      params.set(paramName, term);
    } else {
      params.delete(paramName);
    }
    replace(`${pathname}?${params.toString()}`);
    setIsSearching(false);
  };

  const debouncedUpdate = useDebouncedCallback(
    (...args: unknown[]) => updateSearchParams(args[0] as string),
    debounceMs,
  );

  const setQuery = (value: string) => {
    setLocalQuery(value);
    setIsSearching(true);
    debouncedUpdate(value);
  };

  const clearQuery = () => {
    setLocalQuery('');
    setIsSearching(false);
    updateSearchParams(''); // Immediate update on clear
  };

  return {
    query: localQuery,
    urlQuery,
    setQuery,
    clearQuery,
    isSearching,
  };
}
